/*
 * ACCOUNTS PAGE
 *
 * Provides account list/table views, filters, schema selection, export, and account creation.
 * Recent changes: Consolidated synchronized account browsing under the RLP option,
 * hiding Franchise as a separate list choice while preserving schema identity.
 */

import { FC, useEffect, useState, useRef, useCallback } from 'react';
import { THEME_CLASSES } from '../constants/themeConstants';
import { SchemaProvider, type Schema } from '../context/SchemaContext';
import NewAccount from '../components/account/NewAccount';
import useDebounce from '../hooks/useDebounce';
import AccountList from '../components/account/AccountList';
import AccountTable from '../components/account/AccountTable';
import SchemaSelector from '../components/common/SchemaSelector';
import ExportButton from '../components/common/ExportButton';
import Spinner from '../components/common/Spinner';
import { useLocation, useNavigate } from 'react-router';
import { useTeams } from '../context/TeamContext';
import PageLayout from '../components/patterns/PageLayout';
import { Button } from '../components/primitives';

const formatAccountSchemaLabel = (schema: Schema) => {
  const conciseLabel = schema.name.replace(/\baccount\b/gi, ' ').replace(/\s+/g, ' ').trim();
  return conciseLabel || schema.name;
};

export const canViewAccountSchema = (
  schema: Schema,
  teams: Array<{ teamId: number }>,
) => {
  const normalizedName = schema.name.trim().toLowerCase();

  if (normalizedName === 'franchise account') {
    return false;
  }

  return normalizedName === 'rlp account'
    || teams.some(team => team.teamId === schema.teamId);
};

// Inner component that uses SchemaContext
const AccountsContent: FC = () => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const activeTab: 'all' | 'my' = pathname.endsWith('/all') ? 'all' : 'my';
  const { teams, isLoading: isLoadingUser } = useTeams();

  const accountSchemaFilter = useCallback(
    (schema: Schema) => canViewAccountSchema(schema, teams),
    [teams],
  );

  // Check for create=true query parameter
  useEffect(() => {
    const params = new URLSearchParams(search);
    if (params.get('create') === 'true') {
      setIsNewAccountOpen(true);
    }
  }, [search]);

  // UI state
  const [searchQuery, setSearchQuery] = useState(() => localStorage.getItem('accounts-search') || '');
  const [isNewAccountOpen, setIsNewAccountOpen] = useState(false);
  const [selectedSchema, setSelectedSchema] = useState<Schema | null>(null);
  const [rowCount, setRowCount] = useState(0);

  // Export state
  const [exportData, setExportData] = useState<any[]>([]);
  const [exportColumns, setExportColumns] = useState<Array<{field: string, headerName: string}>>([]);

  // Stable callback functions to prevent unnecessary re-renders
  const handleDataChange = useCallback((data: any[], columns: Array<{field: string, headerName: string}>) => {
    setExportData(data);
    setExportColumns(columns);
  }, []);

  const handleClose = () => {
    // Remove query parameters while keeping the path
    const url = new URL(window.location.href);
    url.searchParams.delete('create');
    navigate(pathname + url.search);

    setIsNewAccountOpen(false);
  };

  // Debounce search query
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const accountCountLabel = `${rowCount} ${rowCount === 1 ? 'account' : 'accounts'}`;

  // Handle initial tab restoration on mount
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      const storedTab = localStorage.getItem('accounts-tab');
      if (storedTab && storedTab !== activeTab) {
        navigate(storedTab === 'all' ? '/accounts/all' : '/accounts/my');
      }
      isInitialMount.current = false;
    }
  }, [navigate, activeTab]);

  // Persist search and tab in localStorage
  useEffect(() => {
    localStorage.setItem('accounts-search', searchQuery);
    localStorage.setItem('accounts-tab', activeTab);
  }, [searchQuery, activeTab]);

  if (isLoadingUser) {
    return (
      <div className={`p-6 ${THEME_CLASSES.text.primary}`}>
        <div className="flex items-center justify-center h-64">
          <Spinner />
          <span className="ml-3">Loading user data...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageLayout>
        {/* header */}
        <div className="mx-4 flex flex-col gap-3 sm:mx-6 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-wrap items-center gap-3 xl:shrink-0 xl:flex-nowrap">
            <h1 className={`text-xl font-semibold my-auto ${THEME_CLASSES.text.primary}`}>Accounts</h1>
            {activeTab === 'all' && (
              <Button
                onClick={() => setIsNewAccountOpen(true)}
                size="sm"
                className="min-h-9"
                aria-label="New account"
                leftIcon={(
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M10 3a1 1 0 0 1 1 1v5h5a1 1 0 1 1 0 2h-5v5a1 1 0 1 1-2 0v-5H4a1 1 0 1 1 0-2h5V4a1 1 0 0 1 1-1z" clipRule="evenodd" />
                  </svg>
                )}
              >
                <span className="xl:hidden 2xl:inline">New account</span>
              </Button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 xl:shrink-0 xl:flex-nowrap">
            <div className={`inline-flex h-9 rounded-xl border ${THEME_CLASSES.card.border} ${THEME_CLASSES.card.background} p-1 shadow-sm`}>
              <button
                type="button"
                onClick={() => navigate('/accounts/my')}
                className={`shrink-0 whitespace-nowrap rounded-lg px-3 py-1 text-sm font-medium transition-colors ${activeTab === 'my'
                  ? `${THEME_CLASSES.button.primary} shadow-sm`
                  : `${THEME_CLASSES.text.secondary} hover:bg-slate-100 dark:hover:bg-slate-900`
                  }`}
              >
                My Accounts
              </button>
              <button
                type="button"
                onClick={() => navigate('/accounts/all')}
                className={`shrink-0 whitespace-nowrap rounded-lg px-3 py-1 text-sm font-medium transition-colors ${activeTab === 'all'
                  ? `${THEME_CLASSES.button.primary} shadow-sm`
                  : `${THEME_CLASSES.text.secondary} hover:bg-slate-100 dark:hover:bg-slate-900`
                  }`}
              >
                All Accounts
              </button>
            </div>
            <div className="shrink-0 whitespace-nowrap rounded-full bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300" aria-live="polite">
              {accountCountLabel}
            </div>
            {activeTab === 'all' && selectedSchema && (
              <div
                id="grid-column-selector-portal"
                className="inline-flex xl:hidden 2xl:inline-flex"
              />
            )}
          </div>
          <div className="relative grid w-full grid-cols-1 gap-2 sm:grid-cols-2 xl:w-auto xl:shrink-0 xl:grid-cols-[13rem_12rem_auto] xl:items-center 2xl:grid-cols-[15rem_12rem_auto]">
            {/* Search box */}
            <div className="relative w-full">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg className={`h-4 w-4 ${THEME_CLASSES.text.secondary}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8 4a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM2 8a6 6 0 1 1 10.89 3.476l4.817 4.817a1 1 0 0 1-1.414 1.414l-4.816-4.816A6 6 0 0 1 2 8z" clipRule="evenodd" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search accounts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full pl-11 pr-8 py-2 border rounded-md text-xs font-medium focus:outline-none focus:ring-1 focus:ring-blue-500 ${searchQuery ? 'border-red-500' : THEME_CLASSES.input.border} ${THEME_CLASSES.input.background} ${THEME_CLASSES.input.text} ${THEME_CLASSES.input.placeholder}`}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  aria-label="Clear account search"
                >
                  <svg className={`h-4 w-4 ${THEME_CLASSES.text.secondary} hover:${THEME_CLASSES.text.primary}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 1 1 1.414 0L10 8.586l4.293-4.293a1 1 0 1 1 1.414 1.414l-4.293 4.293 4.293 4.293a1 1 0 0 1-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 0 1-1.414-1.414l4.293-4.293-4.293-4.293a1 1 0 0 1 0-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              )}
            </div>
            {/* Schema Selector */}
            {activeTab === 'all' && (
              <SchemaSelector
                onSchemaChange={setSelectedSchema}
                entityType={2}
                label="Team"
                placeholder="Select team"
                ariaLabel="Select account team"
                className="w-full"
                storageKey="accounts-team"
                schemaFilter={accountSchemaFilter}
                formatSchemaLabel={formatAccountSchemaLabel}
              />
            )}
            {/* Export Button */}
            <ExportButton
              data={exportData}
              columns={exportColumns}
              filename={activeTab === 'all' ? 'accounts-export' : 'my-accounts-export'}
              disabled={rowCount === 0}
            />
          </div>
        </div>

        {/* main area */}
        <div className="relative min-h-0 flex-1">
          {activeTab === 'all' ? (
            selectedSchema ? (
              <AccountTable
                searchQuery={debouncedSearchQuery}
                schema={selectedSchema}
                onRowCountChange={setRowCount}
                onDataChange={handleDataChange}
                onClearSearch={() => setSearchQuery('')}
                onCreateAccount={() => setIsNewAccountOpen(true)}
              />
            ) : (
              <div className={`flex h-64 flex-col items-center justify-center gap-3 px-6 text-center ${THEME_CLASSES.text.secondary}`}>
                <h2 className={`text-base font-semibold ${THEME_CLASSES.text.primary}`}>Select a team to view accounts</h2>
                <p className="max-w-md text-sm">Use the Team selector above to choose the account list you want to review.</p>
              </div>
            )
          ) : (
            <AccountList
              searchValue={debouncedSearchQuery}
              onRowCountChange={setRowCount}
              onDataChange={handleDataChange}
              onBrowseAllAccounts={() => navigate('/accounts/all')}
              onClearSearch={() => setSearchQuery('')}
            />
          )}
        </div>
      </PageLayout>
      <NewAccount isOpen={isNewAccountOpen} onClose={handleClose} />
    </>
  );
};

const Accounts: FC = () => {
  return (
    <SchemaProvider>
      <AccountsContent />
    </SchemaProvider>
  );
};

export default Accounts;
